/**
 * ProductCardExpandedBlock Component
 *
 * Custom BlockNote React component for detailed product comparison cards
 * with pros/cons lists and customizable feature ratings.
 *
 * Features:
 * - Title and description fields (description supports markdown)
 * - Optional product image with upload support
 * - 5 customizable feature sections (title + value)
 * - Pros/cons rich text lists
 * - Optional badge display
 * - CTA button with customizable text
 * - Edit mode with form fields
 * - View mode with rendered preview
 * - Image upload integration (500px variant)
 * - Responsive design for side-by-side comparison
 *
 * Props (from block.props):
 * - title: Product name (required)
 * - description: Product overview (required, supports markdown)
 * - productImageUrl: Optional product image URL
 * - linkUrl: CTA link URL (required)
 * - linkText: CTA button text (required)
 * - badge: Optional badge text
 * - features: Array of 5 feature objects with title and value
 * - review: Optional detailed review text (supports markdown)
 * - pros: Array of positive points
 * - cons: Array of negative points
 *
 * Markdown Support:
 * - Users can type markdown syntax in the Description field
 * - On blur, markdown is converted to HTML for storage and rendering
 * - Supports detection of existing HTML to prevent double-conversion
 * - If props contain HTML (e.g., from SSR), plain text is extracted for editing
 * - Supported syntax: **bold**, *italic*, [link](url), paragraphs, line breaks
 *
 * Related Files:
 * - app/components/blocknote/blocks/product_card_expanded_component.rb
 * - app/javascript/controllers/blocks_text_editor_controller.js
 * - app/components/block/pros_cons_component.rb
 * - app/components/block/badge_component.rb
 */

import React, { useState, useEffect, useCallback } from "react";
import ImageUploadField from "./shared/ImageUploadField.jsx";
import { normalizeWebUrl, sanitizeUrl, sanitizeHtml } from "../../utils/sanitize";
import { markdownToHtml, htmlToMarkdown } from "../../utils/markdown";

// Rating options from 0 to 5 in 0.5 increments
const RATING_OPTIONS = [
  { value: "", label: "No rating" },
  { value: "0", label: "0 stars" },
  { value: "0.5", label: "0.5 stars" },
  { value: "1", label: "1 star" },
  { value: "1.5", label: "1.5 stars" },
  { value: "2", label: "2 stars" },
  { value: "2.5", label: "2.5 stars" },
  { value: "3", label: "3 stars" },
  { value: "3.5", label: "3.5 stars" },
  { value: "4", label: "4 stars" },
  { value: "4.5", label: "4.5 stars" },
  { value: "5", label: "5 stars" },
];

/**
 * Star Rating Display Component
 *
 * Renders stars based on rating value (0-5, supports half stars)
 * @param {number} rating - The rating value (0-5)
 * @param {string} uniqueId - Unique identifier for SVG gradient (prevents ID collisions)
 */
function StarRating({ rating, uniqueId = "default" }) {
  if (!rating && rating !== 0) return null;

  const numRating = parseFloat(rating);
  if (isNaN(numRating)) return null;

  const fullStars = Math.floor(numRating);
  const hasHalfStar = numRating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
  const gradientId = `halfStar-${uniqueId}`;

  return (
    <span
      className="inline-flex items-center text-yellow-500"
      aria-label={`${numRating} out of 5 stars`}
    >
      {/* Full stars */}
      {Array(fullStars)
        .fill(null)
        .map((_, i) => (
          <svg
            key={`full-${i}`}
            className="w-4 h-4"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      {/* Half star */}
      {hasHalfStar && (
        <svg className="w-4 h-4" viewBox="0 0 20 20">
          <defs>
            <linearGradient id={gradientId}>
              <stop offset="50%" stopColor="currentColor" />
              <stop offset="50%" stopColor="#D1D5DB" />
            </linearGradient>
          </defs>
          <path
            fill={`url(#${gradientId})`}
            d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
          />
        </svg>
      )}
      {/* Empty stars */}
      {Array(emptyStars)
        .fill(null)
        .map((_, i) => (
          <svg
            key={`empty-${i}`}
            className="w-4 h-4 text-neutral-300"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
    </span>
  );
}

/**
 * Feature Section Component
 *
 * Renders a single feature with title, value (supports markdown), and rating fields in edit mode
 */
function FeatureSection({
  index,
  feature,
  onUpdate,
  isEditMode,
  valueMarkdown,
  onValueMarkdownChange,
  onValueBlur,
}) {
  if (!isEditMode) {
    return (
      <div className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
        <span className="text-sm font-medium text-neutral-700">
          {feature.title || `Feature ${index + 1}`}
        </span>
        <div className="flex items-center gap-2">
          {feature.rating && (
            <StarRating rating={feature.rating} uniqueId={`feature-${index}`} />
          )}
          {feature.value && (
            <span
              className="text-sm text-neutral-900 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(feature.value) }}
            />
          )}
          {!feature.rating && !feature.value && (
            <span className="text-sm text-neutral-400">-</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-3 pb-2 border-b border-gray-300 last:border-b-0">
      <div className="grid grid-cols-12 gap-2 mb-2">
        <input
          type="text"
          value={feature.title || ""}
          onChange={(e) => onUpdate(index, "title", e.target.value)}
          placeholder={`Feature ${index + 1} Title`}
          className="form-control col-span-8"
          maxLength={100}
        />
        <select
          value={feature.rating || ""}
          onChange={(e) => onUpdate(index, "rating", e.target.value)}
          className="form-control col-span-4"
        >
          {RATING_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-12 gap-2 mb-2">
        <textarea
          value={valueMarkdown}
          onChange={(e) => onValueMarkdownChange(index, e.target.value)}
          onBlur={() => onValueBlur(index)}
          placeholder="Value (optional, supports **bold**, *italic*, [links](url))"
          className="form-control col-span-12"
          maxLength={500}
          rows={3}
        />
      </div>
    </div>
  );
}

/**
 * Pros/Cons List Editor Component
 *
 * Simple text area for entering pros/cons as line-separated items
 * Supports custom labels to override default "Pros" and "Cons" headings
 */
function ProsConsList({
  items,
  label,
  customLabel,
  onChange,
  isEditMode,
  type,
}) {
  const colorClass = type === "pros" ? "text-green-600" : "text-red-600";
  const iconSymbol = type === "pros" ? "✓" : "✗";
  // Use custom label if provided, otherwise fall back to default label
  const displayLabel = customLabel && customLabel.trim() ? customLabel : label;

  // Local state to preserve line breaks while typing
  const [localValue, setLocalValue] = React.useState(() =>
    items ? items.join("\n") : "",
  );

  // Sync local state when items prop changes (e.g., after drag/drop)
  React.useEffect(() => {
    const newValue = items ? items.join("\n") : "";
    // Only update if the filtered content is different (avoid cursor position issues)
    const currentFiltered = localValue
      .split("\n")
      .filter((line) => line.trim());
    const newFiltered = items || [];
    if (JSON.stringify(currentFiltered) !== JSON.stringify(newFiltered)) {
      setLocalValue(newValue);
    }
  }, [items]);

  if (!isEditMode) {
    return (
      <div className="mt-4">
        <h4 className={`text-sm font-semibold mb-2 ${colorClass}`}>
          {displayLabel}
        </h4>
        <ul className="space-y-1">
          {items && items.length > 0 ? (
            items.map((item, index) => (
              <li key={index} className="flex items-start text-sm">
                <span className={`mr-2 ${colorClass}`}>{iconSymbol}</span>
                <span className="text-neutral-700">{item}</span>
              </li>
            ))
          ) : (
            <li className="text-sm text-neutral-400 italic">
              No {type} listed
            </li>
          )}
        </ul>
      </div>
    );
  }

  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 mb-1">
        {label}
      </label>
      <textarea
        value={localValue}
        onChange={(e) => {
          // Update local state immediately (preserves line breaks while typing)
          setLocalValue(e.target.value);
        }}
        onBlur={() => {
          // Filter out empty lines only when saving (on blur)
          const lines = localValue.split("\n").filter((line) => line.trim());
          onChange(lines);
        }}
        placeholder={`Enter ${type}, one per line`}
        className="form-control"
        rows={4}
      />
    </div>
  );
}

/**
 * Image Upload Component (Wrapper for shared component)
 *
 * Handles image upload for product cards using the shared ImageUploadField
 */
function ImageUpload({ imageUrl, onImageChange, isEditMode, blockId }) {
  // Handle image upload callback
  const handleImageUploaded = (url, variants) => {
    // Use the 500px variant for product cards, or fall back to original
    const finalUrl = variants?.["500px"] || url;
    onImageChange(finalUrl);
  };

  if (!isEditMode && imageUrl) {
    return (
      <img
        src={imageUrl}
        alt="Product"
        className="w-16 h-16 object-contain rounded"
      />
    );
  }

  if (!isEditMode) return null;

  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 mb-2">
        Product Image (Optional)
      </label>
      <ImageUploadField
        currentImageUrl={imageUrl}
        onImageUploaded={handleImageUploaded}
        blockType="productCardExpanded"
        blockId={blockId}
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/avif"
        maxSize={5 * 1024 * 1024}
      />
    </div>
  );
}

/**
 * Main ProductCardExpandedBlock Component
 */
export default function ProductCardExpandedBlock({ block, editor }) {
  // Helper function to detect if a string contains HTML tags
  const containsHtml = (str) => {
    if (!str) return false;
    return /<\/?[a-z][\s\S]*>/i.test(str);
  };

  // Initialize features with 5 sections
  const getDefaultFeatures = () => {
    return Array(5)
      .fill(null)
      .map(() => ({
        title: "",
        value: "",
        rating: "",
      }));
  };

  // Normalize features array to always have 5 items
  const normalizeFeatures = (features) => {
    if (!Array.isArray(features) || features.length === 0) {
      return getDefaultFeatures();
    }
    // Ensure we have exactly 5 features
    const normalized = [...features];
    while (normalized.length < 5) {
      normalized.push({ title: "", value: "", rating: "" });
    }
    return normalized.slice(0, 5).map((f) => ({
      title: f?.title || "",
      value: f?.value || "",
      rating: f?.rating || "",
    }));
  };

  const [isEditMode, setIsEditMode] = useState(editor?.isEditable ?? false);
  const [touched, setTouched] = useState({
    title: false,
    description: false,
    linkUrl: false,
  });
  const [collapsed, setCollapsed] = useState(() => {
    const p = block.props || {};
    const hasContent = Boolean(
      (p.description && p.description.trim()) ||
        (p.productImageUrl && p.productImageUrl.trim()) ||
        (p.linkUrl && p.linkUrl.trim()) ||
        (p.linkText && p.linkText.trim() && p.linkText !== "Learn More") ||
        (p.badge && p.badge.trim()) ||
        (Array.isArray(p.features) &&
          p.features.some(
            (f) =>
              f &&
              ((f.title && f.title.trim()) ||
                (f.value && f.value.trim()) ||
                (f.rating && f.rating.trim())),
          )) ||
        (Array.isArray(p.pros) && p.pros.length > 0) ||
        (Array.isArray(p.cons) && p.cons.length > 0),
    );
    return hasContent; // collapse by default if existing content
  });

  // Local state for all editable fields
  const [localProps, setLocalProps] = useState({
    title: block.props?.title || "",
    description: block.props?.description || "",
    productImageUrl: block.props?.productImageUrl || "",
    linkUrl: block.props?.linkUrl || "",
    linkText: block.props?.linkText ?? "", // Use ?? to allow empty string
    badge: block.props?.badge || "",
    features: normalizeFeatures(block.props?.features),
    review: block.props?.review || "",
    prosLabel: block.props?.prosLabel || "",
    consLabel: block.props?.consLabel || "",
    pros: Array.isArray(block.props?.pros) ? block.props.pros : [],
    cons: Array.isArray(block.props?.cons) ? block.props.cons : [],
  });

  // Initialize markdown state - if props contain HTML, convert back to markdown
  // This allows users to see and edit the original markdown syntax
  const [descriptionMarkdown, setDescriptionMarkdown] = useState(() => {
    const desc = block.props?.description || "";
    return containsHtml(desc) ? htmlToMarkdown(desc) : desc;
  });

  // Initialize markdown state for feature values
  // Each feature value can contain markdown that is converted to HTML on blur
  const [featuresMarkdown, setFeaturesMarkdown] = useState(() => {
    const features = normalizeFeatures(block.props?.features);
    return features.map((f) => {
      const val = f?.value || "";
      return containsHtml(val) ? htmlToMarkdown(val) : val;
    });
  });

  // Initialize markdown state for review field
  // If props contain HTML, convert back to markdown for editing
  const [reviewMarkdown, setReviewMarkdown] = useState(() => {
    const review = block.props?.review || "";
    return containsHtml(review) ? htmlToMarkdown(review) : review;
  });

  // Sync local state when block.props changes (e.g., after drag/drop reorder)
  useEffect(() => {
    const features = normalizeFeatures(block.props?.features);
    setLocalProps({
      title: block.props?.title || "",
      description: block.props?.description || "",
      productImageUrl: block.props?.productImageUrl || "",
      linkUrl: block.props?.linkUrl || "",
      linkText: block.props?.linkText ?? "",
      badge: block.props?.badge || "",
      features: features,
      review: block.props?.review || "",
      prosLabel: block.props?.prosLabel || "",
      consLabel: block.props?.consLabel || "",
      pros: Array.isArray(block.props?.pros) ? block.props.pros : [],
      cons: Array.isArray(block.props?.cons) ? block.props.cons : [],
    });
    // Also sync the markdown state for description
    const desc = block.props?.description || "";
    setDescriptionMarkdown(containsHtml(desc) ? htmlToMarkdown(desc) : desc);
    // Also sync the markdown state for feature values
    setFeaturesMarkdown(
      features.map((f) => {
        const val = f?.value || "";
        return containsHtml(val) ? htmlToMarkdown(val) : val;
      }),
    );
    // Also sync the markdown state for review
    const review = block.props?.review || "";
    setReviewMarkdown(containsHtml(review) ? htmlToMarkdown(review) : review);
  }, [block.id, block.props]);

  // Detect edit/view mode
  useEffect(() => {
    const editMode = editor && editor.isEditable;
    setIsEditMode(editMode);
  }, [editor]);

  // Update local state and sync to BlockNote
  const handleFieldChange = useCallback(
    (key, value) => {
      // Update local state
      setLocalProps((prev) => ({ ...prev, [key]: value }));

      // Sync to BlockNote
      if (!editor) return;
      const currentBlock = editor.getBlock(block.id);
      if (!currentBlock) {
        console.error("Block not found:", block.id);
        return;
      }

      editor.updateBlock(currentBlock, {
        props: { ...currentBlock.props, [key]: value },
      });
    },
    [editor, block.id],
  );

  // Update a specific feature (for title and rating - non-markdown fields)
  const updateFeature = useCallback(
    (index, field, value) => {
      const newFeatures = [...localProps.features];
      if (!newFeatures[index]) {
        newFeatures[index] = { title: "", value: "", rating: "" };
      }
      newFeatures[index] = { ...newFeatures[index], [field]: value };
      handleFieldChange("features", newFeatures);
    },
    [localProps.features, handleFieldChange],
  );

  // Update feature value markdown (local state only, shown in textarea)
  const handleFeatureValueMarkdownChange = useCallback((index, markdown) => {
    setFeaturesMarkdown((prev) => {
      const updated = [...prev];
      updated[index] = markdown;
      return updated;
    });
  }, []);

  // Convert feature value markdown to HTML on blur and sync to BlockNote
  const handleFeatureValueBlur = useCallback(
    (index) => {
      const markdown = featuresMarkdown[index] || "";
      const html = markdownToHtml(markdown);
      const newFeatures = [...localProps.features];
      if (!newFeatures[index]) {
        newFeatures[index] = { title: "", value: "", rating: "" };
      }
      newFeatures[index] = { ...newFeatures[index], value: html };
      handleFieldChange("features", newFeatures);
    },
    [featuresMarkdown, localProps.features, handleFieldChange],
  );

  // Edit Mode: Form Fields
  if (isEditMode) {
    const isInvalidUrl = (() => {
      const normalized = normalizeWebUrl(localProps.linkUrl || "");
      const sanitized = sanitizeUrl(normalized);
      return (
        !(localProps.linkUrl && localProps.linkUrl.trim()) || sanitized === "#"
      );
    })();
    const titleError = !(localProps.title && localProps.title.trim());
    // Validate against the markdown input (what user sees), not the HTML output
    const descError = !(descriptionMarkdown && descriptionMarkdown.trim());
    return (
      <div className="product-card-expanded-editor w-full p-4 border border-gray-200 rounded-lg bg-gray-50">
        <div className="mb-4">
          <span className="sr-only block text-base font-semibold mb-4">
            Product Card (Expanded)
          </span>

          {/* Product Image */}
          <ImageUpload
            imageUrl={localProps.productImageUrl}
            onImageChange={(url) => handleFieldChange("productImageUrl", url)}
            isEditMode={true}
            blockId={block.id}
          />

          {/* Title */}
          <div className="mb-3">
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Title (Required)
            </label>
            <input
              type="text"
              value={localProps.title}
              onChange={(e) => handleFieldChange("title", e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, title: true }))}
              placeholder="Product name"
              className={`form-control${touched.title && titleError ? " error" : ""}`}
              maxLength={200}
            />
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setCollapsed((v) => !v)}
                className="text-sm text-blue-600 hover:text-blue-800 inline-flex items-center"
                aria-expanded={!collapsed}
                aria-controls={`product-card-expanded-section-${block.id}`}
              >
                <svg
                  className={`w-4 h-4 mr-1 transform transition-transform ${collapsed ? "" : "rotate-90"}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M6 6l6 4-6 4V6z"
                    clipRule="evenodd"
                  />
                </svg>
                {collapsed ? "Show details" : "Hide details"}
              </button>
            </div>
          </div>

          {/* Description */}
          {!collapsed && (
            <div id={`product-card-expanded-section-${block.id}`}>
              <div className="mb-3">
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Description (Required)
                </label>
                <textarea
                  value={descriptionMarkdown}
                  onChange={(e) => setDescriptionMarkdown(e.target.value)}
                  onBlur={() => {
                    setTouched((t) => ({ ...t, description: true }));
                    const html = markdownToHtml(descriptionMarkdown);
                    handleFieldChange("description", html);
                  }}
                  placeholder="Product overview (supports **bold**, *italic*, and [links](url))"
                  className={`form-control${touched.description && descError ? " error" : ""}`}
                  rows={3}
                  maxLength={1000}
                />
                <span className="text-xs text-neutral-500 mt-1 block">
                  Supports markdown: **bold**, *italic*, [link](url)
                </span>
              </div>

              {/* Badge */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Badge (Optional)
                </label>
                <input
                  type="text"
                  value={localProps.badge}
                  onChange={(e) => handleFieldChange("badge", e.target.value)}
                  placeholder="e.g., Best Value"
                  className="form-control"
                  maxLength={50}
                />
              </div>

              {/* Features */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Features (5 Sections)
                </label>
                {localProps.features.map((feature, index) => (
                  <FeatureSection
                    key={`${block.id}-feature-${index}`}
                    index={index}
                    feature={feature}
                    onUpdate={updateFeature}
                    isEditMode={true}
                    valueMarkdown={featuresMarkdown[index] || ""}
                    onValueMarkdownChange={handleFeatureValueMarkdownChange}
                    onValueBlur={handleFeatureValueBlur}
                  />
                ))}
              </div>

              {/* Review */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Review (Optional)
                </label>
                <textarea
                  value={reviewMarkdown}
                  onChange={(e) => setReviewMarkdown(e.target.value)}
                  onBlur={() => {
                    const html = markdownToHtml(reviewMarkdown);
                    handleFieldChange("review", html);
                  }}
                  placeholder="Write a detailed review (supports **bold**, *italic*, and [links](url))"
                  className="form-control"
                  rows={6}
                />
                <span className="text-xs text-neutral-500 mt-1 block">
                  Supports markdown: **bold**, *italic*, [link](url)
                </span>
              </div>

              {/* Pros/Cons Labels (Optional Override) */}
              <div className="grid grid-cols-2 gap-4 mb-3 mt-2">
                <div>
                  <input
                    type="text"
                    value={localProps.prosLabel}
                    onChange={(e) =>
                      handleFieldChange("prosLabel", e.target.value)
                    }
                    placeholder="e.g., Key Features, Advantages"
                    className="form-control"
                    maxLength={50}
                  />
                  <span className="text-xs text-neutral-500 mt-1 block">
                    Leave empty to use "Pros"
                  </span>
                </div>
                <div>
                  <input
                    type="text"
                    value={localProps.consLabel}
                    onChange={(e) =>
                      handleFieldChange("consLabel", e.target.value)
                    }
                    placeholder="e.g., Limitations, Drawbacks"
                    className="form-control"
                    maxLength={50}
                  />
                  <span className="text-xs text-neutral-500 mt-1 block">
                    Leave empty to use "Cons"
                  </span>
                </div>
              </div>

              {/* Pros/Cons */}
              <div className="grid grid-cols-2 gap-4 mb-3">
                <ProsConsList
                  items={localProps.pros}
                  label="Pros"
                  customLabel={localProps.prosLabel}
                  onChange={(items) => handleFieldChange("pros", items)}
                  isEditMode={true}
                  type="pros"
                />
                <ProsConsList
                  items={localProps.cons}
                  label="Cons"
                  customLabel={localProps.consLabel}
                  onChange={(items) => handleFieldChange("cons", items)}
                  isEditMode={true}
                  type="cons"
                />
              </div>

              {/* Link */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Link URL (Required)
                  </label>
                  <input
                    type="url"
                    value={localProps.linkUrl}
                    onChange={(e) =>
                      handleFieldChange("linkUrl", e.target.value)
                    }
                    onBlur={() => {
                      setTouched((t) => ({ ...t, linkUrl: true }));
                      handleFieldChange(
                        "linkUrl",
                        normalizeWebUrl(localProps.linkUrl),
                      );
                    }}
                    placeholder="https://example.com or example.com"
                    className={`form-control${touched.linkUrl && isInvalidUrl ? " error" : ""}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Button Text
                  </label>
                  <input
                    type="text"
                    value={localProps.linkText}
                    onChange={(e) =>
                      handleFieldChange("linkText", e.target.value)
                    }
                    placeholder="e.g., Download Now"
                    className="form-control"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // View Mode: Rendered Card
  return (
    <div className="product-card-expanded-viewer max-w-md mx-auto">
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {/* Badge */}
        {localProps.badge && (
          <div className="px-6 pt-4">
            <span className="inline-flex items-center px-2 py-1 rounded-md text-sm font-medium bg-orange-500 text-white">
              {localProps.badge}
            </span>
          </div>
        )}

        {/* Header with optional image */}
        <div className="px-6 py-4">
          <div className="flex items-start gap-3">
            {localProps.productImageUrl && (
              <ImageUpload
                imageUrl={localProps.productImageUrl}
                onImageChange={() => {}}
                isEditMode={false}
                blockId={block.id}
              />
            )}
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-neutral-900">
                {localProps.title || "Product Title"}
              </h3>
              <div
                className="mt-1 text-sm text-neutral-600 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(localProps.description || "Product description"),
                }}
              />
            </div>
          </div>
        </div>

        {/* Features */}
        {localProps.features.some((f) => f.title || f.value || f.rating) && (
          <div className="px-6 py-3 border-t border-gray-100">
            {localProps.features.map((feature, index) =>
              feature.title || feature.value || feature.rating ? (
                <FeatureSection
                  key={`${block.id}-view-feature-${index}`}
                  index={index}
                  feature={feature}
                  isEditMode={false}
                />
              ) : null,
            )}
          </div>
        )}

        {/* Review */}
        {localProps.review && (
          <div className="px-6 py-4 border-t border-gray-100">
            <h4 className="text-sm font-semibold text-neutral-900 mb-2">
              Review
            </h4>
            <div
              className="text-sm text-neutral-700 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(localProps.review) }}
            />
          </div>
        )}

        {/* Pros/Cons */}
        {(localProps.pros.length > 0 || localProps.cons.length > 0) && (
          <div className="px-6 py-4 border-t border-gray-100">
            <div className="grid grid-cols-2 gap-4">
              <ProsConsList
                items={localProps.pros}
                label="Pros"
                customLabel={localProps.prosLabel}
                onChange={() => {}}
                isEditMode={false}
                type="pros"
              />
              <ProsConsList
                items={localProps.cons}
                label="Cons"
                customLabel={localProps.consLabel}
                onChange={() => {}}
                isEditMode={false}
                type="cons"
              />
            </div>
          </div>
        )}

        {/* CTA Button */}
        {localProps.linkUrl && (
          <div className="px-6 pb-4">
            <a
              href={localProps.linkUrl}
              target="_blank"
              rel="noopener"
              className="block w-full text-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            >
              {localProps.linkText || "Learn More"}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
